import { OrdersI } from '../interfaces/orders.interface'
import { OrdersService } from '../services/orders.service'
import { UpdateAdditionalServicesPayloadT } from '../types/additional_services.types'
import {
    DownloadInsuranceResponse,
    GetCostFilesT,
    GetInvoiceProblemsT,
    GetInvoiceStatusHistory,
    GetPeriodsT,
    GetShortcomingFilesT,
    GetTemperatureRegimesT,
} from '../types/create_order.types'
import {
    ArrestInvoicePayload,
    CallcenterOrderPayload,
    FTLOrderPayloadI,
    FTLOrdersGetListParamsT,
    FTLOrdersGetListResponseT,
    GetCourierAppDeliveryCancelReasonsResponse,
    GetFtlOrderDelayResponse,
    GetFTLOrderResponseT,
    GetInvoiceCouriersResponseT,
    GetInvoiceTripsInfo,
    OrderPayloadI,
    OrdersDownloadFTLPayloadT,
    OrdersDownloadFTLResponseT,
    OrdersGetInvoiceForPrintResponseT,
    OrdersGetInvoiceHistoryResponseT,
    OrdersGetInvoiceResponseT,
    OrdersGetInvoiceStatusesT,
    OrdersGetListParamsT,
    OrdersGetListResponseT,
    OrdersGetOrderForPrintResponseT,
    OrdersGetOrderInvoicesResponseT,
    OrdersGetOrderResponseT,
    OrdersGetPreviousStatuses,
    RestoreInvoicePayload,
    SendDispatcherCommentInvoiceT,
    SendDispatcherCommentOrderT,
    SetDeclaredPricePayloadT,
    setNewPredictDateParams,
    UpdateOrderPayloadI,
} from '../types/orders.types'

const ordersService = new OrdersService()

class OrdersRealisation implements OrdersI {
    getList(params: OrdersGetListParamsT): Promise<OrdersGetListResponseT> {
        return ordersService.getList(params)
    }

    getFTLOrders(params: FTLOrdersGetListParamsT): Promise<FTLOrdersGetListResponseT> {
        return ordersService.getFTLOrders(params)
    }

    getOrder(order_id: number): Promise<OrdersGetOrderResponseT> {
        return ordersService.getOrder(order_id)
    }

    getFTLOrder(order_id: number): Promise<GetFTLOrderResponseT> {
        return ordersService.getFTLOrder(order_id)
    }

    getFtlOrderDelay(order_id: number): Promise<GetFtlOrderDelayResponse> {
        return ordersService.getFtlOrderDelay(order_id)
    }

    setCommentFtlOrder(order_id: number, commentId: number, stateId: number): Promise<void> {
        return ordersService.setCommentFtlOrder(order_id, commentId, stateId)
    }

    getOrderInvoices(order_id: number, params: any): Promise<OrdersGetOrderInvoicesResponseT> {
        return ordersService.getOrderInvoices(order_id, params)
    }

    getOrderInvoicesForPrint(order_id: number): Promise<OrdersGetOrderInvoicesResponseT> {
        return ordersService.getOrderInvoicesForPrint(order_id)
    }

    getOrderForPrint(order_id: number): Promise<OrdersGetOrderForPrintResponseT> {
        return ordersService.getOrderForPrint(order_id)
    }

    getInvoice(invoice_id: number): Promise<OrdersGetInvoiceResponseT> {
        return ordersService.getInvoice(invoice_id)
    }

    getInvoiceTripsInfo(invoice_id: number): Promise<GetInvoiceTripsInfo> {
        return ordersService.getInvoiceTripsInfo(invoice_id)
    }

    getLTLInvoiceForPrint(invoice_id: number): Promise<OrdersGetInvoiceResponseT> {
        return ordersService.getLTLInvoiceForPrint(invoice_id)
    }

    getInvoiceByInvoiceNumber(invoice_number: number): Promise<OrdersGetInvoiceResponseT> {
        return ordersService.getInvoiceByInvoiceNumber(invoice_number)
    }

    updateInvoiceNumber(invoice_id: number, invoice_number: string): Promise<void> {
        return ordersService.updateInvoiceNumber(invoice_id, invoice_number)
    }

    setNewPredictDate(invoice_id: number, params: setNewPredictDateParams): Promise<void> {
        return ordersService.setNewPredictDate(invoice_id, params)
    }

    verify(invoice_id: number, verify: number): Promise<void> {
        return ordersService.verify(invoice_id, verify)
    }

    setPreviousStatus(invoice_id: number, code: string): Promise<void> {
        return ordersService.setPreviousStatus(invoice_id, code)
    }

    sendCommentOrder(payload: SendDispatcherCommentOrderT): Promise<void> {
        return ordersService.sendCommentOrder(payload)
    }

    sendCommentInvoice(payload: SendDispatcherCommentInvoiceT): Promise<void> {
        return ordersService.sendCommentInvoice(payload)
    }

    getInvoiceForPrint(invoice_number: string): Promise<OrdersGetInvoiceForPrintResponseT> {
        return ordersService.getInvoiceForPrint(invoice_number)
    }

    getInvoiceDeliveryHistory(invoice_number: string): Promise<OrdersGetInvoiceHistoryResponseT> {
        return ordersService.getInvoiceDeliveryHistory(invoice_number)
    }

    cancelOrder(order_id: number): Promise<void> {
        return ordersService.cancelOrder(order_id)
    }

    cancelInvoice(invoice_id: number): Promise<void> {
        return ordersService.cancelInvoice(invoice_id)
    }

    arrestInvoice(invoice_id: number, payload: ArrestInvoicePayload): Promise<void> {
        return ordersService.arrestInvoice(invoice_id, payload)
    }

    approveSelfDelivery(invoice_id: number): Promise<void> {
        return ordersService.approveSelfDelivery(invoice_id)
    }

    approveDelivery(invoice_id: number, payload: FormData): Promise<void> {
        return ordersService.approveDelivery(invoice_id, payload)
    }

    restoreInvoice(invoice_id: number, payload: RestoreInvoicePayload | undefined): Promise<void> {
        return ordersService.restoreInvoice(invoice_id, payload)
    }

    createOrder(payload: OrderPayloadI): Promise<void> {
        return ordersService.createOrder(payload)
    }

    updateOrder(order_id: number, payload: UpdateOrderPayloadI): Promise<void> {
        return ordersService.updateOrder(order_id, payload)
    }

    createFTLOrder(payload: FTLOrderPayloadI): Promise<void> {
        return ordersService.createFTLOrder(payload)
    }

    createCallcenterOrder(payload: CallcenterOrderPayload): Promise<void> {
        return ordersService.createCallcenterOrder(payload)
    }

    getInvoiceStatuses(invoice_number: string): Promise<OrdersGetInvoiceStatusesT> {
        return ordersService.getInvoiceStatuses(invoice_number)
    }

    getPreviousStatuses(): Promise<OrdersGetPreviousStatuses> {
        return ordersService.getPreviousStatuses()
    }

    setDeclaredPrice(id: number, payload: SetDeclaredPricePayloadT): Promise<void> {
        return ordersService.setDeclaredPrice(id, payload)
    }

    updateOrderAdditionalServices(
        id: number,
        payload: UpdateAdditionalServicesPayloadT
    ): Promise<void> {
        return ordersService.updateOrderAdditionalServices(id, payload)
    }

    createOrderFromExcel(payload: FormData): Promise<void> {
        return ordersService.createOrderFromExcel(payload)
    }

    createMultipleOrderFromExcel(payload: FormData): Promise<void> {
        return ordersService.createMultipleOrderFromExcel(payload)
    }

    createMultipleFTLOrderFromExcel(payload: FormData): Promise<void> {
        return ordersService.createMultipleFTLOrderFromExcel(payload)
    }

    exportExcelFTL(params: OrdersDownloadFTLPayloadT): Promise<Blob> {
        return ordersService.exportExcelFTL(params)
    }

    exportExcelCompanyFTL(params: OrdersDownloadFTLPayloadT): Promise<OrdersDownloadFTLResponseT> {
        return ordersService.exportExcelCompanyFTL(params)
    }

    importActNumbers(payload: FormData): Promise<void> {
        return ordersService.importActNumbers(payload)
    }

    getTemperatureRegimes(): Promise<GetTemperatureRegimesT> {
        return ordersService.getTemperatureRegimes()
    }

    getPeriods(): Promise<GetPeriodsT> {
        return ordersService.getPeriods()
    }

    getInvoiceProblems(invoiceId: number): Promise<GetInvoiceProblemsT> {
        return ordersService.getInvoiceProblems(invoiceId)
    }

    getInvoiceStatusHistory(invoiceNumber: string): Promise<GetInvoiceStatusHistory> {
        return ordersService.getInvoiceStatusHistory(invoiceNumber)
    }

    saveBox(invoiceId: number, packCode: string): Promise<void> {
        return ordersService.saveBox(invoiceId, packCode)
    }

    getShortcomingFiles(orderId: number): Promise<GetShortcomingFilesT> {
        return ordersService.getShortcomingFiles(orderId)
    }

    getCostFilesDeliveries(invoiceId: number): Promise<GetCostFilesT> {
        return ordersService.getCostFilesDeliveries(invoiceId)
    }

    getCostFilesTakes(orderId: number): Promise<GetCostFilesT> {
        return ordersService.getCostFilesTakes(orderId)
    }

    downloadInsurance(fileUuid: string): Promise<DownloadInsuranceResponse> {
        return ordersService.downloadInsurance(fileUuid)
    }

    resendInvoiceDeliveryStatusToOneC(invoiceId: number): Promise<void> {
        return ordersService.resendInvoiceDeliveryStatusToOneC(invoiceId)
    }

    getInvoiceCouriers(invoiceId: number): Promise<GetInvoiceCouriersResponseT> {
        return ordersService.getInvoiceCouriers(invoiceId)
    }

    getCancelReasons(companyId: number): Promise<GetCourierAppDeliveryCancelReasonsResponse> {
        return ordersService.getCancelReasons(companyId)
    }

    updateProperties(invoiceId: number): Promise<void> {
        return ordersService.updateProperties(invoiceId)
    }
}

export const orders = new OrdersRealisation()
